export const FUNCTION_TIMEOUTS = {
  query: 30_000,
  mutation: 30_000,
  httpAction: 300_000,
  cron: 600_000,
} as const;

export type FunctionType = keyof typeof FUNCTION_TIMEOUTS;

type LoggerLike = Pick<Console, "warn" | "error">;

export type FunctionTimeoutEvent = {
  type: FunctionType;
  functionName: string;
  timeoutMs: number;
};

export type FunctionTimeoutRestartPolicy = {
  enabled: boolean;
  threshold: number;
  windowMs: number;
  delayMs: number;
  kinds: FunctionType[];
};

export type FunctionTimeoutRestartRuntime = {
  env?: Record<string, string | undefined>;
  logger?: LoggerLike;
  nowMs?: number;
  setTimeoutImpl?: typeof setTimeout;
  exitImpl?: (code: number) => never | void;
  diagnostics?: () => Record<string, unknown> | undefined;
};

export type FunctionTimeoutRestartDiagnostic = {
  reason: "repeated_function_timeouts";
  count: number;
  threshold: number;
  windowMs: number;
  windowAgeMs: number;
  delayMs: number;
  functions: string[];
  latest: FunctionTimeoutEvent;
  pid: number;
  diagnostics?: Record<string, unknown>;
};

const DEFAULT_TIMEOUT_RESTART_THRESHOLD = 3;
const DEFAULT_TIMEOUT_RESTART_WINDOW_MS = 60_000;
const DEFAULT_TIMEOUT_RESTART_DELAY_MS = 250;
const DEFAULT_TIMEOUT_RESTART_KINDS: FunctionType[] = ["query", "mutation"];
const DEFAULT_FUNCTION_SLOW_LOG_MS = 10_000;

let timeoutWindowStartMs = 0;
let timeoutCountInWindow = 0;
let timeoutRestartScheduled = false;
let timeoutFunctionNames: string[] = [];
let functionInvocationSequence = 0;

function parsePositiveInteger(value: string | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function parseNonNegativeInteger(value: string | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

function parseRestartKinds(value: string | undefined): FunctionType[] {
  if (!value?.trim()) return [...DEFAULT_TIMEOUT_RESTART_KINDS];
  const allowed = new Set<FunctionType>(["query", "mutation", "httpAction", "cron"]);
  const kinds = value
    .split(",")
    .map((part) => part.trim())
    .filter((part): part is FunctionType => allowed.has(part as FunctionType));
  return kinds.length > 0 ? kinds : [...DEFAULT_TIMEOUT_RESTART_KINDS];
}

export function resolveFunctionTimeoutRestartPolicy(
  env: Record<string, string | undefined> = process.env,
): FunctionTimeoutRestartPolicy {
  const explicit = env.GENCOW_FUNCTION_TIMEOUT_RESTART_ENABLED;
  const enabled =
    explicit === "true" ? true : explicit === "false" ? false : Boolean(env.GENCOW_METERING_URL);

  return {
    enabled,
    threshold: parsePositiveInteger(
      env.GENCOW_FUNCTION_TIMEOUT_RESTART_THRESHOLD,
      DEFAULT_TIMEOUT_RESTART_THRESHOLD,
    ),
    windowMs: parsePositiveInteger(
      env.GENCOW_FUNCTION_TIMEOUT_RESTART_WINDOW_MS,
      DEFAULT_TIMEOUT_RESTART_WINDOW_MS,
    ),
    delayMs: parsePositiveInteger(
      env.GENCOW_FUNCTION_TIMEOUT_RESTART_DELAY_MS,
      DEFAULT_TIMEOUT_RESTART_DELAY_MS,
    ),
    kinds: parseRestartKinds(env.GENCOW_FUNCTION_TIMEOUT_RESTART_KINDS),
  };
}

export function resolveFunctionSlowLogMs(env: Record<string, string | undefined> = process.env): number {
  return parseNonNegativeInteger(env.GENCOW_FUNCTION_SLOW_LOG_MS, DEFAULT_FUNCTION_SLOW_LOG_MS);
}

export function buildFunctionTimeoutRestartDiagnostic(params: {
  event: FunctionTimeoutEvent;
  policy: FunctionTimeoutRestartPolicy;
  count: number;
  windowStartMs: number;
  nowMs: number;
  functions: string[];
  pid?: number;
  diagnostics?: Record<string, unknown>;
}): FunctionTimeoutRestartDiagnostic {
  return {
    reason: "repeated_function_timeouts",
    count: params.count,
    threshold: params.policy.threshold,
    windowMs: params.policy.windowMs,
    windowAgeMs: Math.max(0, params.nowMs - params.windowStartMs),
    delayMs: params.policy.delayMs,
    functions: params.functions,
    latest: params.event,
    pid: params.pid ?? process.pid,
    ...(params.diagnostics ? { diagnostics: params.diagnostics } : {}),
  };
}

function collectRestartDiagnostics(
  runtime: FunctionTimeoutRestartRuntime,
): Record<string, unknown> | undefined {
  try {
    return runtime.diagnostics?.();
  } catch {
    return { diagnostics: "unavailable" };
  }
}

function nextFunctionInvocationId(): string {
  functionInvocationSequence =
    functionInvocationSequence >= Number.MAX_SAFE_INTEGER ? 1 : functionInvocationSequence + 1;
  return `${Date.now().toString(36)}-${functionInvocationSequence}`;
}

function formatErrorForRuntimeLog(error: unknown): string {
  const record = error as { code?: unknown; message?: unknown; name?: unknown };
  const name = typeof record?.name === "string" ? record.name : "Error";
  const code = typeof record?.code === "string" ? ` code=${record.code}` : "";
  const message =
    typeof record?.message === "string"
      ? record.message.replace(/\s+/g, " ").slice(0, 300)
      : String(error).replace(/\s+/g, " ").slice(0, 300);
  return `name=${name}${code} message="${message}"`;
}

export function recordFunctionTimeout(
  event: FunctionTimeoutEvent,
  runtime: FunctionTimeoutRestartRuntime = {},
): void {
  const env = runtime.env ?? process.env;
  const policy = resolveFunctionTimeoutRestartPolicy(env);
  if (!policy.enabled || !policy.kinds.includes(event.type)) return;

  const logger = runtime.logger ?? console;
  const nowMs = runtime.nowMs ?? Date.now();
  if (!timeoutWindowStartMs || nowMs - timeoutWindowStartMs > policy.windowMs) {
    timeoutWindowStartMs = nowMs;
    timeoutCountInWindow = 0;
    timeoutFunctionNames = [];
  }

  timeoutCountInWindow += 1;
  timeoutFunctionNames.push(`${event.type}:${event.functionName}`);
  logger.warn(
    `[runtime] function timeout ${timeoutCountInWindow}/${policy.threshold} in ${policy.windowMs}ms window: ${event.type} "${event.functionName}"`,
  );

  if (timeoutCountInWindow < policy.threshold || timeoutRestartScheduled) return;

  timeoutRestartScheduled = true;
  const timeoutFunctions = timeoutFunctionNames.slice(-policy.threshold);
  const names = timeoutFunctions.join(", ");
  const diagnostic = buildFunctionTimeoutRestartDiagnostic({
    event,
    policy,
    count: timeoutCountInWindow,
    windowStartMs: timeoutWindowStartMs,
    nowMs,
    functions: timeoutFunctions,
    diagnostics: collectRestartDiagnostics(runtime),
  });
  logger.error(`[runtime] function timeout restart diagnostics ${JSON.stringify(diagnostic)}`);
  logger.error(`[runtime] scheduling app process restart after repeated function timeouts (${names})`);

  const setTimeoutImpl = runtime.setTimeoutImpl ?? setTimeout;
  const exitImpl = runtime.exitImpl ?? ((code: number) => process.exit(code));
  const handle = setTimeoutImpl(() => {
    exitImpl(1);
  }, policy.delayMs);
  if (typeof (handle as { unref?: () => void }).unref === "function") {
    (handle as { unref: () => void }).unref();
  }
}

export function __resetFunctionTimeoutRestartStateForTests(): void {
  timeoutWindowStartMs = 0;
  timeoutCountInWindow = 0;
  timeoutRestartScheduled = false;
  timeoutFunctionNames = [];
  functionInvocationSequence = 0;
}

export async function executeWithTimeout<T>(
  fn: () => Promise<T>,
  type: FunctionType,
  functionName: string,
  timeouts: Record<FunctionType, number> = FUNCTION_TIMEOUTS,
  restartRuntime?: FunctionTimeoutRestartRuntime,
): Promise<T> {
  const timeoutMs = timeouts[type];
  const invocationId = nextFunctionInvocationId();
  const startedAt = Date.now();
  const logger = restartRuntime?.logger ?? console;
  const slowLogMs = resolveFunctionSlowLogMs(restartRuntime?.env);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let timedOut = false;
  const work = Promise.resolve().then(fn);

  work.then(
    () => {
      if (!timedOut) return;
      const elapsedMs = Date.now() - startedAt;
      logger.warn(
        `[runtime] function completed after timeout id=${invocationId} type=${type} name="${functionName}" elapsedMs=${elapsedMs} timeoutMs=${timeoutMs}`,
      );
    },
    (error) => {
      if (!timedOut) return;
      const elapsedMs = Date.now() - startedAt;
      logger.error(
        `[runtime] function failed after timeout id=${invocationId} type=${type} name="${functionName}" elapsedMs=${elapsedMs} timeoutMs=${timeoutMs} ${formatErrorForRuntimeLog(error)}`,
      );
    },
  );

  try {
    const result = await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        controller.signal.addEventListener("abort", () => {
          timedOut = true;
          const elapsedMs = Date.now() - startedAt;
          logger.error(
            `[runtime] function timeout id=${invocationId} type=${type} name="${functionName}" elapsedMs=${elapsedMs} timeoutMs=${timeoutMs}`,
          );
          recordFunctionTimeout({ type, functionName, timeoutMs }, restartRuntime);
          const error = new Error(
            `Function "${functionName}" exceeded ${timeoutMs / 1000}s time limit. ` +
              `Split into smaller mutations and call them sequentially from the frontend. ` +
              `Limits: query 30s, mutation 30s, httpAction 5min, cron 10min.`,
          );
          (error as Error & { code?: string }).code = "FUNCTION_TIMEOUT";
          reject(error);
        });
      }),
    ]);
    const elapsedMs = Date.now() - startedAt;
    if (slowLogMs > 0 && elapsedMs >= slowLogMs) {
      logger.warn(
        `[runtime] function slow id=${invocationId} type=${type} name="${functionName}" elapsedMs=${elapsedMs} thresholdMs=${slowLogMs}`,
      );
    }
    return result;
  } catch (error) {
    const elapsedMs = Date.now() - startedAt;
    const code = (error as Error & { code?: string })?.code;
    if (code !== "FUNCTION_TIMEOUT" && slowLogMs > 0 && elapsedMs >= slowLogMs) {
      logger.warn(
        `[runtime] function slow failed id=${invocationId} type=${type} name="${functionName}" elapsedMs=${elapsedMs} thresholdMs=${slowLogMs} ${formatErrorForRuntimeLog(error)}`,
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
